// src/components/Leaderboard.js
import React, { useState, useEffect } from "react";
import { firestore } from "../firebase";
import { collection, query, onSnapshot, orderBy } from "firebase/firestore";
import { calculateBadges, computeRawStats, ALL_BADGES } from "./BadgeEngine";
import './Leaderboard.css';

export default function Leaderboard() {
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const q = query(collection(firestore, "games"), orderBy("createdAt", "desc"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const allGames = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const userGroups = allGames.reduce((acc, game) => {
        const userId = game.userId || 'unknown';
        if (!acc[userId]) {
          acc[userId] = { 
            name: game.UserName || game.userName || "Player", 
            photo: game.photoURL || "https://via.placeholder.com/40", 
            games: [] 
          };
        }
        acc[userId].games.push(game);
        return acc;
      }, {});

      const leaderData = Object.values(userGroups).map(user => {
        const stats = computeRawStats(user.games);
        const earnedBadgeIds = calculateBadges(user.games);
        return {
          ...user,
          totalGames: user.games.length,
          average: parseFloat((stats.totalScore / user.games.length).toFixed(1)),
          highScore: stats.highScore,
          bestScoreGameId: stats.bestScoreGameId,
          earnedBadgeIds,
          strikePct: stats.strikePct,
          sparePct: stats.sparePct,
          singlePinPct: stats.singlePinPct,
          maxStrikeStreak: stats.maxStrikeStreak
        };
      });

      setPlayers(leaderData.sort((a, b) => b.average - a.average));
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  if (loading) return <div className="lb-loading">Syncing League Standings...</div>;

  return (
    <div className="lb-container">
      <h2 className="neon-title">League Leaderboard</h2>
      <div className="lb-card">
        <table className="lb-table">
          <thead>
            <tr>
              <th>Rank</th>
              <th>Player</th>
              <th className="text-center">Games</th>
              <th className="text-center">Avg</th>
              <th className="text-center">Strikes</th>
              <th className="text-center">Spares</th>
              <th className="text-center">S-Pin</th>
              <th className="text-center">Streak</th>
              <th>Badges</th>
            </tr>
          </thead>
          <tbody>
            {players.map((p, i) => (
              <tr key={p.name + i} className="lb-row">
                <td className="lb-rank">#{i + 1}</td>
                <td className="lb-player">
                  <img src={p.photo} alt="" className="lb-avatar" />
                  <div className="player-meta">
                    <span className="player-name">{p.name}</span>
                    <a href={`/game/${p.bestScoreGameId}`} className="player-high">
                      PB: {p.highScore}
                    </a>
                  </div>
                </td>
                <td className="lb-stat">{p.totalGames}</td>
                <td className="lb-stat highlight">{p.average}</td>
                <td className="lb-stat">{p.strikePct}%</td>
                <td className="lb-stat">{p.sparePct}%</td>
                <td className="lb-stat">{p.singlePinPct}%</td>
                <td className="lb-stat">{p.maxStrikeStreak}</td>
                <td className="lb-badges">
                  {p.earnedBadgeIds.map(bId => {
                    const b = ALL_BADGES.find(x => x.id === bId);
                    return <span key={bId} title={b?.label} className="lb-badge-mini">{b?.icon}</span>;
                  })}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}